import React, {
  useRef,
  ChangeEvent,
  KeyboardEvent,
  ClipboardEvent,
} from 'react';
import { VerificationInputFieldContainer, InputFieldContainerProps } from './style'

type VerificationCodeInputProps = {
  className?: string,
  label?: string;
  iconUrl?: string;
  errorMessage?: string;
  successMessage?: string;
  isRequired?: boolean;
  margin?: string;
  length: number;
  values: string[];
  setValues: (values: string[]) => void;
} & InputFieldContainerProps;



const VerificationInputField: React.FC<VerificationCodeInputProps> = ({
  className,
  label,
  width,
  iconUrl,
  isRequired,
  length,
  labelPosition,
  labelColor,
  labelSize,
  labelWeight,
  inputFontSize,
  inputFontWeight,
  inputColor,
  inputBackgroundColor,
  borderColor,
  inputHeight,
  inputWidth,
  margin,
  error,
  success,
  values,
  setValues
}) => {

    const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

    const handleChange = (
      e: ChangeEvent<HTMLInputElement>,
      idx: number
    ) => {
      const { value, selectionStart } = e.currentTarget;
    
      if (value === '') {
        const newValues = [...values];
        newValues[idx] = '';
        setValues(newValues);
        return;
      }
    
      if (!/^[a-zA-Z0-9]+$/.test(value)) {
        return;
      }
    
      const cursorPosition = selectionStart ?? value.length;
      const character = value.charAt(Math.max(0, cursorPosition - 1));
    
      const newValues = [...values];
      newValues[idx] = character.toUpperCase();
      setValues(newValues);
    
      if (idx < length - 1) {
        inputsRef.current[idx + 1]?.focus();
      }
    };

    const handleKeyDown = (
      e: KeyboardEvent<HTMLInputElement>,
      idx: number
    ) => {

      if (e.ctrlKey || e.metaKey || e.altKey || e.nativeEvent.isComposing) {
        return;
      }
    
      if (/^[a-zA-Z0-9]$/.test(e.key)) {
        e.preventDefault();
    
        const newValues = [...values];
        newValues[idx] = e.key.toUpperCase();
        setValues(newValues);
    
        if (idx < length - 1) {
          inputsRef.current[idx + 1]?.focus();
        }
    
        return;
      }
    
      if (e.key === 'Backspace' && values[idx] === '' && idx > 0) {
        inputsRef.current[idx - 1]?.focus();
      }
    };


    const handlePaste = (
      e: ClipboardEvent<HTMLInputElement>,
      startIdx: number
    ) => {
      e.preventDefault();
    
      const pastedText = e.clipboardData.getData('text/plain');
      const code = pastedText.replace(/\D/g, '');
    
      if (!code) {
        return;
      }
    
      const newValues = [...values];
    
      code
        .slice(0, length - startIdx)
        .split('')
        .forEach((character, index) => {
          newValues[startIdx + index] = character;
        });
    
      setValues(newValues);
    
      const lastFilledIndex = Math.min(
        startIdx + code.length - 1,
        length - 1
      );
    
      inputsRef.current[lastFilledIndex]?.focus();
    };


    return (
        <VerificationInputFieldContainer
          className={className}
          labelPosition={labelPosition}
          labelColor={labelColor}
          labelSize={labelSize}
          labelWeight={labelWeight}
          borderColor={borderColor}
          inputHeight={inputHeight}
          inputWidth={inputWidth}
          inputColor={inputColor}
          inputFontSize={inputFontSize}
          inputFontWeight={inputFontWeight}
          inputBackgroundColor={inputBackgroundColor}
          error={error}
          success={success}
          width={width}
          margin={margin}
        >
          {label?
            <label>
              VERIFICATION CODE
              {isRequired? <span className="required"> *</span>: null}
            </label>
          : null}
          {(iconUrl && iconUrl.length > 0)? <img src={iconUrl} className="icon" alt="check"/>: null}
          <div className='blockContainer'>
            {Array.from({ length }).map((_, idx) => (
              <input
                key={idx}
                type="text"
                value={values[idx] ?? ''}
                onChange={(e) => handleChange(e, idx)}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                onPaste={(e) => handlePaste(e, idx)}
                ref={(el) => (inputsRef.current[idx] = el)}
              />
            ))}
          </div>
        </VerificationInputFieldContainer>
    );
};

export default VerificationInputField;